import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { TEMPLATE_FONT_FAMILY, OptionDropdown, DropdownOption } from "../../Dashboard/common";
import { ChartMetric, MetricToggle } from "../../Dashboard/chartControls";
import { getStageColor } from "../../../../utils/stageColors";
import CompanyIcon from "../../../../assets/company.png";
import {
  SummaryDatePoint,
  SummaryEntityKey,
  SummaryPeriod,
  SummaryProductStat,
  SummaryStageStat,
  SummaryTopEntity,
} from "./summaryReportService";

export { OptionDropdown };
export type { DropdownOption };

const FUNNEL_COLORS = [
  "#3538CD",
  "#06AED4",
  "#FFA201",
  "#0E9384",
  "#27AE60",
  "#E41F07",
  "#8E44AD",
  "#F39C12",
  "#1ABC9C",
  "#E74C3C",
];

const PRODUCT_COLOR = "#824EEB";
const TIMELINE_COLOR = "#4A00E5";
const VALUE_COLOR = "#0E9384";
const TOP_PRODUCTS = 6;
const TITLE_MAX_LENGTH = 32;

const countFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const amountFormatter = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

export const formatAmount = (value: number, currency?: string | null) =>
  `${amountFormatter.format(Number.isFinite(value) ? value : 0)}${currency ? ` ${currency}` : ""}`;

const escapeHtml = (text: string) =>
  text.replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char] || char));

const tooltipBox = (title: string, rows: [string, string][]) => `
  <div class="sr-tooltip">
    <div class="sr-tooltip-title">${escapeHtml(title)}</div>
    ${rows
      .map(([label, value]) => `<div class="sr-tooltip-row">${escapeHtml(label)}: <strong>${escapeHtml(value)}</strong></div>`)
      .join("")}
  </div>
`;

export const SummaryCard = ({
  title,
  icon,
  actions,
  toolbar,
  children,
  bodyClassName = "",
}: {
  title: string;
  icon: string;
  actions?: React.ReactNode;
  toolbar?: React.ReactNode;
  children: React.ReactNode;
  bodyClassName?: string;
}) => (
  <div className="card flex-fill">
    <div className="card-header border-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
      <h5 className="mb-0 fs-18 fw-bold text-dark d-flex align-items-center">
        <span className="sr-card-icon me-2">
          <i className={icon} />
        </span>
        {title}
      </h5>
      {actions && <div className="d-flex align-items-center gap-2 flex-wrap">{actions}</div>}
    </div>
    <div className={`card-body pt-0 ${bodyClassName}`}>
      {toolbar && (
        <div className="sr-card-toolbar d-flex align-items-center justify-content-between flex-wrap gap-2">
          {toolbar}
        </div>
      )}
      {children}
    </div>
  </div>
);

export const EmptyState = ({ message, height }: { message: string; height?: number }) => (
  <div
    className="d-flex flex-column align-items-center justify-content-center text-center py-5 sr-empty"
    style={height ? { minHeight: height } : undefined}
  >
    <i className="ti ti-chart-bar-off fs-1 mb-2" />
    <p className="mb-0">{message}</p>
  </div>
);

export const ChartSkeleton = ({ height, variant = "bars" }: { height: number; variant?: "bars" | "rows" }) =>
  variant === "rows" ? (
    <div className="d-flex flex-column gap-3 py-2" style={{ minHeight: height }}>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className="sr-skeleton" style={{ height: 28 }} />
      ))}
    </div>
  ) : (
    <div className="sr-chart-skeleton" style={{ height }}>
      {[0.9, 0.55, 0.75, 0.35, 0.6, 0.8, 0.45].map((ratio, index) => (
        <span key={index} className="sr-skeleton" style={{ height: `${ratio * 100}%` }} />
      ))}
    </div>
  );

const priorityBadgeClass = (priority: string) => {
  switch (priority.trim().toLowerCase()) {
    case "high":
      return "badge-soft-danger border border-danger";
    case "medium":
      return "badge-soft-warning border border-warning";
    case "low":
      return "badge-soft-success border border-success";
    default:
      return "badge-soft-secondary border border-secondary";
  }
};

const ENTITY_ROUTES: Record<SummaryEntityKey, string> = {
  leads: "/leads",
  pipelines: "/pipelines",
  deals: "/deals",
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

const UserAvatar = ({ name, url }: { name: string; url: string | null }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="avatar avatar-xs avatar-rounded border me-2 sr-user-avatar">
      {url && !failed ? <img src={url} alt={name} onError={() => setFailed(true)} /> : initialsOf(name)}
    </span>
  );
};

const CompanyLogo = ({ name, url }: { name: string; url: string | null }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="avatar avatar-xs avatar-rounded border me-2 bg-white">
      <img className="w-auto h-auto" src={url && !failed ? url : CompanyIcon} alt={name} onError={() => setFailed(true)} />
    </span>
  );
};

const TruncatedTitle = ({ to, text }: { to: string | null; text: string }) => {
  const truncated = text.length > TITLE_MAX_LENGTH;
  const label = truncated ? `${text.slice(0, TITLE_MAX_LENGTH).trimEnd()}...` : text;
  const content = to ? (
    <Link to={to} target="_blank" className="fw-medium text-dark sr-entity-title">
      {label}
    </Link>
  ) : (
    <span className="fw-medium text-dark sr-entity-title">{label}</span>
  );
  if (!truncated) return content;
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`sr-title-${text}`}>{text}</Tooltip>}>
      {content}
    </OverlayTrigger>
  );
};

export const TopEntitiesTable = ({
  entityKey,
  items,
  currency,
  emptyMessage,
}: {
  entityKey: SummaryEntityKey;
  items: SummaryTopEntity[];
  currency: string | null;
  emptyMessage: string;
}) => (
  <div className="table-responsive custom-table sr-top-table">
    <table className="table table-nowrap mb-0">
      <thead className="table-white">
        <tr>
          <th>Title</th>
          <th>Company</th>
          <th>Assigned User</th>
          <th className="text-end">Estimated Value</th>
          <th>Stage</th>
          <th>Priority</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr>
            <td colSpan={6}>
              <EmptyState message={emptyMessage} />
            </td>
          </tr>
        ) : (
          items.map((item, index) => (
            <tr key={`${entityKey}-${item.id ?? index}`}>
              <td>
                <div className="d-flex flex-column align-items-start">
                  <TruncatedTitle
                    to={item.id !== null ? `${ENTITY_ROUTES[entityKey]}/${item.id}` : null}
                    text={item.name || "Untitled"}
                  />
                  <small className="text-muted">{item.source || "N/A"}</small>
                </div>
              </td>
              <td>
                <div className="d-flex align-items-center">
                  <CompanyLogo name={item.companyName || "Company"} url={item.companyLogoUrl} />
                  <span className="sr-ellipsis" title={item.companyName || "N/A"}>
                    {item.companyName || "N/A"}
                  </span>
                </div>
              </td>
              <td>
                <div className="d-flex align-items-center">
                  <UserAvatar name={item.assignedUserName || "Unassigned"} url={item.assignedUserAvatarUrl} />
                  <span className="sr-ellipsis" title={item.assignedUserEmail || item.assignedUserName || "Unassigned"}>
                    {item.assignedUserName || "Unassigned"}
                  </span>
                </div>
              </td>
              <td className="text-end fw-medium text-dark">
                {formatAmount(item.estimatedValue, item.currency || currency)}
              </td>
              <td>
                {item.stage ? (
                  <span className={`badge ${getStageColor(item.stage)} sr-pill`}>{item.stage}</span>
                ) : (
                  "-"
                )}
              </td>
              <td>
                {item.priority ? (
                  <span className={`badge badge-pill ${priorityBadgeClass(item.priority)} sr-pill`}>
                    <i className="ti ti-square-rounded-filled me-1" />
                    {item.priority}
                  </span>
                ) : (
                  "-"
                )}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export type SummaryMetric = ChartMetric;

const compactFormatter = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

const metricOf = (row: { count: number; amount: number }, metric: SummaryMetric) =>
  metric === "count" ? row.count : row.amount;

const formatMetricAxis = (value: number, metric: SummaryMetric, currency: string | null) =>
  metric === "count"
    ? countFormatter.format(Math.round(value))
    : `${compactFormatter.format(Number.isFinite(value) ? value : 0)}${currency ? ` ${currency}` : ""}`;

const niceAxisScale = (value: number, metric: SummaryMetric) => {
  const target = value > 0 ? value * 1.1 : metric === "count" ? 4 : 1000;
  const rawStep = target / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  let step = [1, 2, 2.5, 5, 10].map((multiplier) => multiplier * magnitude).find((candidate) => candidate >= rawStep) ?? magnitude * 10;
  if (metric === "count") step = Math.max(1, Math.ceil(step));
  const tickAmount = Math.max(1, Math.ceil(target / step));
  return { max: step * tickAmount, tickAmount };
};

export const StageFunnelChart = ({
  stages,
  metric,
  currency,
  emptyMessage,
}: {
  stages: SummaryStageStat[];
  metric: SummaryMetric;
  currency: string | null;
  emptyMessage: string;
}) => {
  const rows = useMemo(
    () =>
      stages
        .filter((stage) => stage.stage && metricOf(stage, metric) > 0)
        .sort((a, b) => metricOf(b, metric) - metricOf(a, metric)),
    [stages, metric]
  );

  const options = useMemo<ApexOptions>(
    () => ({
      chart: { type: "bar", toolbar: { show: false }, fontFamily: TEMPLATE_FONT_FAMILY },
      plotOptions: { bar: { borderRadius: 10, horizontal: true, distributed: true, barHeight: "98%", isFunnel: true } },
      colors: FUNNEL_COLORS,
      dataLabels: {
        enabled: true,
        formatter: (_val: number, opt: any) => opt.w.globals.labels[opt.dataPointIndex],
        dropShadow: { enabled: true },
      },
      xaxis: {
        categories: rows.map((row) => `${row.stage} : ${formatMetricAxis(metricOf(row, metric), metric, currency)}`),
      },
      legend: { show: false },
      states: { active: { filter: { type: "none" } } },
      tooltip: {
        custom: ({ dataPointIndex }: any) => {
          const row = rows[dataPointIndex];
          if (!row) return "";
          return tooltipBox(row.stage, [
            ["Count", countFormatter.format(row.count)],
            ["Sum of Estimated Values", formatAmount(row.amount, currency)],
          ]);
        },
      },
    }),
    [rows, metric, currency]
  );

  if (!rows.length) return <EmptyState message={emptyMessage} height={400} />;
  return (
    <Chart
      options={options}
      series={[{ name: "", data: rows.map((row) => metricOf(row, metric)) }]}
      type="bar"
      height={400}
    />
  );
};

export const ProductBarChart = ({
  products,
  seriesName,
  metric,
  currency,
  emptyMessage,
}: {
  products: SummaryProductStat[];
  seriesName: string;
  metric: SummaryMetric;
  currency: string | null;
  emptyMessage: string;
}) => {
  const rows = useMemo(() => {
    const sorted = products
      .filter((product) => product.name.trim())
      .sort((a, b) => metricOf(b, metric) - metricOf(a, metric));
    const head = sorted.slice(0, TOP_PRODUCTS);
    const rest = sorted.slice(TOP_PRODUCTS);
    if (!rest.length) return head;
    return [
      ...head,
      {
        name: "Others",
        imageUrl: null,
        count: rest.reduce((sum, product) => sum + product.count, 0),
        amount: rest.reduce((sum, product) => sum + product.amount, 0),
      },
    ];
  }, [products, metric]);

  const hasData = rows.some((row) => metricOf(row, metric) > 0);

  const options = useMemo<ApexOptions>(
    () => ({
      chart: { type: "bar", toolbar: { show: false }, fontFamily: TEMPLATE_FONT_FAMILY },
      plotOptions: { bar: { horizontal: false, columnWidth: "55%", borderRadius: 4, borderRadiusApplication: "end" } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      colors: [metric === "count" ? PRODUCT_COLOR : VALUE_COLOR],
      fill: { opacity: 1 },
      grid: { borderColor: "#E8E8E8", strokeDashArray: 4 },
      xaxis: {
        categories: rows.map((row) => row.name),
        labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: rows.length > 4, maxHeight: 80, trim: true },
      },
      yaxis: {
        min: 0,
        ...niceAxisScale(Math.max(0, ...rows.map((row) => metricOf(row, metric))), metric),
        labels: { formatter: (val: number) => formatMetricAxis(val, metric, null) },
      },
      tooltip: {
        custom: ({ dataPointIndex }: any) => {
          const row = rows[dataPointIndex];
          if (!row) return "";
          return tooltipBox(row.name, [
            ["Count", countFormatter.format(row.count)],
            ["Value", formatAmount(row.amount, currency)],
          ]);
        },
      },
    }),
    [rows, metric, currency]
  );

  if (!rows.length || !hasData) return <EmptyState message={emptyMessage} height={380} />;
  return (
    <Chart
      options={options}
      series={[{ name: seriesName, data: rows.map((row) => metricOf(row, metric)) }]}
      type="bar"
      height={380}
    />
  );
};

const formatPointLabel = (date: string, period: SummaryPeriod) => {
  if (period === "by_year") return date;
  const parsed = dayjs(date);
  if (!parsed.isValid()) return date;
  return parsed.format(period === "by_week" ? "DD MMM YY" : "MMM YYYY");
};

export const TimelineChart = ({
  points,
  period,
  seriesName,
  metric,
  currency,
  emptyMessage,
}: {
  points: SummaryDatePoint[];
  period: SummaryPeriod;
  seriesName: string;
  metric: SummaryMetric;
  currency: string | null;
  emptyMessage: string;
}) => {
  const hasData = points.some((point) => metricOf(point, metric) > 0);
  const maxValue = Math.max(0, ...points.map((point) => metricOf(point, metric)));
  const seriesLabel = metric === "count" ? seriesName : `${seriesName} Value`;

  const options = useMemo<ApexOptions>(
    () => ({
      chart: { type: "area", zoom: { enabled: false }, toolbar: { show: false }, fontFamily: TEMPLATE_FONT_FAMILY },
      colors: [metric === "count" ? TIMELINE_COLOR : VALUE_COLOR],
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 2 },
      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 100] } },
      grid: { borderColor: "#E8E8E8", strokeDashArray: 4 },
      markers: { size: 0, hover: { size: 4 } },
      xaxis: {
        categories: points.map((point) => formatPointLabel(point.date, period)),
        tickPlacement: "on",
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: "11px" } },
        tooltip: { enabled: false },
      },
      yaxis: {
        min: 0,
        ...niceAxisScale(maxValue, metric),
        labels: { formatter: (val: number) => formatMetricAxis(val, metric, currency) },
      },
      legend: { show: false },
      tooltip: {
        custom: ({ dataPointIndex }: any) => {
          const point = points[dataPointIndex];
          if (!point) return "";
          return tooltipBox(formatPointLabel(point.date, period), [
            [seriesName, countFormatter.format(point.count)],
            ["Value", formatAmount(point.amount, currency)],
          ]);
        },
      },
    }),
    [points, period, maxValue, metric, seriesName, currency]
  );

  if (!hasData) return <EmptyState message={emptyMessage} height={300} />;
  return (
    <Chart
      options={options}
      series={[{ name: seriesLabel, data: points.map((point) => metricOf(point, metric)) }]}
      type="area"
      height={300}
    />
  );
};

export { MetricToggle };
